import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { InfoForm } from '../../Transfers/_components/Layout';
import { LoanPageHeader } from '../_components/LoanPageHeader';
import { buildLoanPaymentTimelineEvents } from '~/app/pages/Loans/Payment/_utils/loanTimeline';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, PageHeader, RenderFields } from '~/components';
import { TimelineEvents } from '~/components/TimelineEvents';
import { useBankAccount, useEntity } from '~/hooks';
import { useNavigate } from '~/lib/navigation';
import { Loan, LoanPayment, LoanRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

interface Params {
  id: string;
}

interface ExtendedLoanPayment extends LoanPayment {
  createdAt?: string;
  effectiveAt?: string;
  idempotencyKey?: string;
  isOffline?: boolean;
}

const formatData = (payment: ExtendedLoanPayment): Record<string, unknown> => {
  const formatted: Record<string, unknown> = {};
  Object.entries(payment).forEach(([key, value]) => {
    formatted[key] = value;
  });
  return formatted;
};

export const PageLoansPaymentView: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { isAuthorized } = useSessionStore();
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();

  const {
    response: bankAccount,
    isLoading: isBankAccountLoading,
    createRequest: fetchBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.LOANS);
    },
  });

  const { response: entity, createRequest: fetchEntity } = useEntity({
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });

  const [payment, setPayment] = useState<ExtendedLoanPayment | undefined>();
  const [loan, setLoan] = useState<Loan | undefined>();
  const [isPaymentLoading, setIsPaymentLoading] = useState(true);
  const hasFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id || hasFetchedRef.current === id) return;

    hasFetchedRef.current = id;
    setPayment(undefined);
    setLoan(undefined);
    setIsPaymentLoading(true);
    LoanRepository.getPayment(id)
      .then((response) => {
        setPayment(response as ExtendedLoanPayment);

        if (response.bankAccountId) {
          fetchBankAccount({ id: response.bankAccountId })
            .then((currentBankAccount) => {
              if (!currentBankAccount) {
                return;
              }

              if (isAuthorized({ permission: 'entities', permissionLevel: 'read' }) && currentBankAccount.owners?.[0]) {
                fetchEntity({ id: currentBankAccount.owners[0] });
              }
            })
            .catch((error) => {
              addDangerNotification({ content: (error as Error).message });
            });
        }

        if (response.loanId) {
          LoanRepository.get(response.loanId)
            .then((loanResponse) => {
              setLoan(loanResponse);
            })
            .catch((error) => {
              addDangerNotification({ content: error.message });
            });
        }
      })
      .catch((error) => {
        hasFetchedRef.current = null;
        addDangerNotification({ content: error.message });
        navigate(ROUTE.LOANS);
      })
      .finally(() => {
        setIsPaymentLoading(false);
      });
  }, [id]);

  const formattedPayment = useMemo(() => (payment ? formatData(payment) : undefined), [payment]);

  const formMethods = useForm<ExtendedLoanPayment>();

  useEffect(() => {
    if (payment) {
      formMethods.setValue('description', payment.description || '-');
      formMethods.setValue('status', payment.status || '-');
      if ('idempotencyKey' in payment) {
        formMethods.setValue('idempotencyKey', (payment as any).idempotencyKey || '-');
      }
    }
  }, [payment, formMethods]);

  const isDataLoading = useMemo(() => {
    return isBankAccountLoading || isPaymentLoading;
  }, [isBankAccountLoading, isPaymentLoading]);

  return (
    <>
      <PageHeader text="Payments" backButton />

      <Loading isLoading={isDataLoading} fullHeight>
        {payment && (
          <LoanPageHeader
            amount={payment.principalAmount!}
            paymentDate={payment.effectiveAt || payment.createdAt}
            status={payment.status!}
            loanId={payment.id!}
            loanType="payment"
            currencyCode={payment.currency}
            isOffline={payment.isOffline ?? false}
          />
        )}
        {bankAccount && payment && (
          <TimelineEvents
            entries={buildLoanPaymentTimelineEvents({
              payment,
              loan,
              bankAccount,
              entityType:
                entity?.type && entity.type !== 'TYPE_NONE'
                  ? (entity.type.toLowerCase() as 'person' | 'business')
                  : undefined,
              navigate,
            })}
          />
        )}
        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<ExtendedLoanPayment>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value || '-')} />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Details
              label="Loan Payment Details"
              formattedData={formattedPayment}
              rawData={payment as Record<string, unknown> | undefined}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
